import {
  type RulerParams,
  type Tick,
  computeHorizontalRulerTicks,
  computeVerticalRulerTicks,
} from '@/lib/ruler/compute-ruler-ticks'

/**
 * Ruler ticks for the whole scrollable range, from layout values the caller has already measured
 * (`originX`/`originY` = canvas origin in scroll-content px, `boardW`/`boardH` = scrollable
 * content size, `viewWidth`/`viewHeight` = scroller client size).
 */
export function computeRulerTickLayout({
  rulerUnit,
  scale,
  originX,
  originY,
  viewWidth,
  viewHeight,
  boardW,
  boardH,
  horizontalZeroAtCanvasX,
  verticalZeroAtCanvasY,
}: {
  rulerUnit: RulerParams['unit']
  scale: number
  originX: number
  originY: number
  viewWidth: number
  viewHeight: number
  boardW: number
  boardH: number
  horizontalZeroAtCanvasX: number
  verticalZeroAtCanvasY: number
}): { h: Tick[]; v: Tick[]; horizontalStripMinPx: number; verticalStripMinPx: number } {
  // Tick positions use pos = origin + u * scale (u = document canvas coords). Map full scroll
  // range [0, boardW] from measured origin — not boardW/2 - bleed/2, which drifts from the
  // real laid-out origin at extreme zoom and shortens the tick span on the ruler.
  const safeScale = scale > 0 ? scale : 1
  const canvasStartX = -originX / safeScale
  const canvasEndX = (boardW - originX) / safeScale
  const canvasStartY = -originY / safeScale
  const canvasEndY = (boardH - originY) / safeScale

  const params: RulerParams = {
    unit: rulerUnit,
    scale,
    originX,
    originY,
    canvasStartX,
    canvasEndX,
    canvasStartY,
    canvasEndY,
    viewportWidth: viewWidth,
    viewportHeight: viewHeight,
    horizontalZeroAtCanvasX,
    verticalZeroAtCanvasY,
  }

  const xSpanPx = Math.ceil((canvasEndX - canvasStartX) * safeScale)
  const ySpanPx = Math.ceil((canvasEndY - canvasStartY) * safeScale)

  return {
    h: computeHorizontalRulerTicks(params),
    v: computeVerticalRulerTicks(params),
    horizontalStripMinPx: Math.max(boardW, xSpanPx + 2),
    verticalStripMinPx: Math.max(boardH, ySpanPx + 2),
  }
}

/** Tick length across a ruler of `majorLength` px: major = full, mid = 8px, minor = 4px. */
export function rulerTickLength(tick: Tick, majorLength: number): number {
  const level = tick.level ?? (tick.isMajor ? 'major' : 'minor')

  if (level === 'major') return majorLength
  if (level === 'mid') return 8

  return 4
}
